import { z } from "zod";
import {
  BRAZIL_UFS,
  GNRE_LOTE_MAX_GUIDES,
  type GnreBlockerCode,
  type GnreKind,
  type GnreRevenue,
  type GnreSpGuide,
} from "@cx/domain";
import { listQuery } from "./common";
import { ulidParam } from "./ids";
import { businessDate } from "./prices";

// ===== GNRE e DARE-SP (T2.11, RF-VEN-11) =====

const nullish = <T extends z.ZodType>(s: T) => s.nullish().transform((v) => v ?? null);

export const GNRE_STATUSES = ["pending", "issued", "paid", "canceled"] as const;
export type GnreStatus = (typeof GNRE_STATUSES)[number];
export const GNRE_ST_REGISTRATIONS_MAX = 27;

/** IE de substituto tributário: só dígitos (pontos e traços saem), até 14. */
const stIe = z
  .string("Informe a inscrição")
  .transform((s) => s.replace(/[\s./-]/g, ""))
  .pipe(z.string().regex(/^\d{2,14}$/, "Inscrição estadual: de 2 a 14 dígitos"));

export const gnreSettingsInput = z
  .object({
    /** IE de substituto tributário da empresa por UF (sem guia por operação nessas UFs). */
    stRegistrations: z
      .array(z.object({ uf: z.enum(BRAZIL_UFS, "UF inválida"), ie: stIe }))
      .max(GNRE_ST_REGISTRATIONS_MAX)
      .default([]),
    /** Guia para SP: GNRE (padrão) ou DARE-SP. */
    spGuide: z.enum(["gnre", "dare_sp"]).default("gnre"),
  })
  .superRefine((v, ctx) => {
    const seen = new Set<string>();
    v.stRegistrations.forEach((r, k) => {
      if (seen.has(r.uf))
        ctx.addIssue({ code: "custom", path: ["stRegistrations", k, "uf"], message: "UF repetida" });
      seen.add(r.uf);
    });
  });
export type GnreSettingsInput = z.output<typeof gnreSettingsInput>;
export type GnreSettingsView = {
  stRegistrations: { uf: string; ie: string }[];
  spGuide: GnreSpGuide;
  companyUf: string | null;
};

export const gnreGenerateInput = z.object({ fiscalDocumentId: ulidParam });
export type GnreGenerateInput = z.output<typeof gnreGenerateInput>;

export const gnreUpdateInput = z.object({ dueDate: businessDate });
export type GnreUpdateInput = z.output<typeof gnreUpdateInput>;

export const gnreIssueInput = z.object({
  /** Código de barras (44) ou linha digitável (48); pontos, espaços e hífens são ignorados. */
  barcode: z
    .string("Informe o código de barras ou a linha digitável")
    .trim()
    .min(1, "Informe o código de barras ou a linha digitável")
    .max(80, "Código de barras inválido"),
  /** Número de controle do Portal GNRE (ou do DARE). */
  controlNumber: nullish(
    z
      .string()
      .trim()
      .max(30, "Número de controle: até 30 caracteres")
      .regex(/^[0-9A-Za-z./-]*$/, "Número de controle: só letras, números, ponto, barra e hífen")
      .transform((s) => s || null),
  ),
});
export type GnreIssueInput = z.output<typeof gnreIssueInput>;

const cents = (what: string) =>
  z.number(`Informe ${what}`).int("Use centavos inteiros").min(0, `${what} não pode ser negativo`);

export const gnrePayInput = z.object({
  paidAt: businessDate,
  financialAccountId: ulidParam,
  paymentMethodId: nullish(ulidParam),
  /** Acréscimos pagos fora do vencimento (o principal é sempre o valor da guia). */
  interestCents: cents("os juros").max(99_999_999_999).default(0),
  fineCents: cents("a multa").max(99_999_999_999).default(0),
});
export type GnrePayInput = z.output<typeof gnrePayInput>;

export const gnreCancelInput = z.object({
  reason: z
    .string("Informe o motivo")
    .trim()
    .min(5, "Motivo: ao menos 5 caracteres")
    .max(255, "Motivo: até 255 caracteres"),
});
export type GnreCancelInput = z.output<typeof gnreCancelInput>;

export const gnreLoteInput = z.object({
  ids: z
    .array(ulidParam, "Selecione as guias")
    .min(1, "Selecione ao menos uma guia")
    .max(GNRE_LOTE_MAX_GUIDES, `No máximo ${GNRE_LOTE_MAX_GUIDES} guias por lote`)
    .refine((a) => new Set(a).size === a.length, "Guia repetida"),
});
export type GnreLoteInput = z.output<typeof gnreLoteInput>;

export const gnreListQuery = listQuery.extend({
  status: z.enum([...GNRE_STATUSES, "open", "all"]).default("all"),
  uf: z.enum(BRAZIL_UFS).optional(),
  fiscalDocumentId: ulidParam.optional(),
});
export type GnreListQuery = z.output<typeof gnreListQuery>;

export type GnreListItem = {
  id: string;
  kind: GnreKind;
  uf: string;
  revenue: GnreRevenue;
  revenueCode: string;
  amountCents: number;
  dueDate: string;
  status: GnreStatus;
  environment: number;
  fiscalDocumentId: string;
  invoiceNumber: number;
  invoiceSeries: number;
  accessKey: string;
  /** A NF-e foi cancelada depois da guia (estorne a guia ou peça restituição). */
  invoiceCanceled: boolean;
  recipientName: string | null;
  payableId: string | null;
  paidAt: string | null;
  createdAt: string;
};
export type GnreList = {
  data: GnreListItem[];
  nextCursor: string | null;
  /** Só na primeira página. */
  summary: { count: number; openCents: number } | null;
};

export type GnreView = GnreListItem & {
  referencePeriod: string;
  barcode: string | null;
  digitableLine: string | null;
  controlNumber: string | null;
  issuedAt: string | null;
  payable: { id: string; status: string; dueDate: string; amountCents: number; paidCents: number } | null;
  proof: { fileName: string; mime: string; uploadedAt: string } | null;
  canceledAt: string | null;
  cancelReason: string | null;
  /** XML do lote só para GNRE pendente ou emitida (DARE-SP é emitido no portal da SEFAZ-SP). */
  xmlAvailable: boolean;
  editable: boolean;
};

export type GnreInvoicePlan = {
  fiscalDocumentId: string;
  invoiceStatus: string;
  candidates: {
    kind: GnreKind;
    uf: string;
    revenue: GnreRevenue;
    revenueCode: string;
    amountCents: number;
    dueDate: string;
    /** Guia já gerada (não cancelada) para esta UF/receita. */
    guideId: string | null;
  }[];
  blockers: { code: GnreBlockerCode; message: string }[];
  guides: GnreListItem[];
};

export type GnreGenerateResult = { created: string[]; existing: string[]; plan: GnreInvoicePlan };

export type GnreFileLink = { url: string; fileName: string };

/** Dados para o PDF de conferência (API). */
export type GnrePrint = {
  id: string;
  kind: GnreKind;
  status: GnreStatus;
  environment: number;
  uf: string;
  revenue: GnreRevenue;
  revenueCode: string;
  amountCents: number;
  dueDate: string;
  referencePeriod: string;
  barcode: string | null;
  digitableLine: string | null;
  controlNumber: string | null;
  invoiceNumber: number;
  invoiceSeries: number;
  accessKey: string;
  emitter: { legalName: string; cnpj: string | null; ie: string | null; address: string; uf: string | null };
  recipient: { name: string; document: string | null; address: string } | null;
};

export const GNRE_PROOF_MAX_BYTES = 5 * 1024 * 1024;
export const GNRE_PROOF_MIME = ["application/pdf", "image/jpeg", "image/png"] as const;
